<template>
  <div :class="containerClass">
    <base-label
      :label="label"
      :labelClasses="labelClasses"
      :disabled="disabled"
      :required="required"
      :tooltip="tooltip"
    />
    <base-read-only-field
      v-if="isReadOnly"
      :value="readOnlyValue"
      :icon="iconForReadOnlyValue"
      :tooltip="readOnlyTooltip"
      :read-only-classes="readOnlyClasses"
    />
    <multiselect
      v-else
      :class="multiselectClass"
      :value="localValue"
      :options="filteredOptions"
      :show-labels="false"
      :hide-selected="hideSelected"
      :allow-empty="allowEmpty"
      :option-height="40"
      :max-height="maxHeight"
      :open-direction="direction"
      :disabled="disabled"
      :multiple="multiple"
      :placeholder="placeholder"
      :searchable="isSearchable"
      :internal-search="false"
      :loading="isLoading"
      @search-change="onChangeSearch"
      @select="onOptionSelected"
    >
      <template #placeholder>
        <div class="flex-grow break-words ms-4 mt-1">
          {{ placeholder }}
        </div>
      </template>
      <template #singleLabel="props">
        <div
          id="shortcut-picker_multiselect-single-label"
          :class="'flex text-base items-center ' + dropDownClass"
        >
          <template v-if="localValue !== 'undefined'">
            <svg-icon
              v-if="entryIcon(props.option) && entryIcon(props.option).length"
              class="icon-3xl ms-2"
              :name="entryIcon(props.option)"
            />
            <div
              class="truncate ms-2"
              :class="{ 'ms-4': !entryIcon(props.option) }"
            >
              {{ entryName(props.option) }}
            </div>
          </template>
        </div>
      </template>
      <template #option="{ option }">
        <div
          class="py-2 px-4 flex flex-col h-full justify-center"
          :class="[
            option.id === localValue ? selectedItemClass : '',
            option.entryClass,
            !option.description ? 'w-full' : 'flex-grow',
          ]"
        >
          <div class="flex items-center">
            <svg-icon
              v-if="showIconInOption(option)"
              :class="option.iconClass || 'icon-3xl me-2 -ms-2'"
              :name="entryIcon(option.id)"
              :style="
                option.$isDisabled ? 'filter: grayscale(100%) opacity(50%)' : ''
              "
            />
            <div
              :class="[
                listItemTitleClass,
                option.$isDisabled ? 'text-gray-300' : '',
                option.indent ? 'ms-4' : '',
              ]"
            >
              {{ option.name ? option.name : $t("Common.notSet") }}
            </div>
          </div>
          <div
            v-if="option.description"
            class="flex flex-grow whitespace-pre-line ms-4 text-start"
            :class="option.$isDisabled ? 'text-gray-300' : ''"
          >
            {{ option.description }}
          </div>
        </div>
      </template>
      <template #noResult>
        <div class="py-3 px-4 flex flex-col h-full justify-center">
          {{ $t("Common.Search.notFound") }}
        </div>
      </template>
      <template #loading>
        <loading-icon
          v-show="isLoading"
          icon-class="icon-lg absolute me-3 end-0 bg-white"
        />
      </template>
    </multiselect>
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import BaseLabel from "@/components/ui/BaseLabel.vue";
import BaseReadOnlyField from "@/components/ui/display-fields/BaseReadOnlyField.vue";
import LoadingIcon from "@/components/ui/LoadingIcon.vue";
import { tr } from "@/utils/translation";

export default {
  components: { LoadingIcon, BaseLabel, BaseReadOnlyField },
  props: {
    // The currently selected id
    // We use value as it is the default vue keyword for dropdowns and permits to use the model prop.
    value: {
      type: String,
      required: false,
    },
    containerClass: {
      type: String,
      default: "",
    },
    iconForReadOnlyValue: {
      type: String,
      required: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    readOnlyTooltip: {
      type: String,
      default: "",
    },
    readOnlyClasses: {
      type: String,
      default: "",
    },
    // Object for which each property is an id, and has a object with 'name' and 'icon' properties as a value.
    // Ids are emitted as an 'select' event when selected.
    // E.g.: { myFirstId: { id: "myFirstId", name: "firstValue", icon: "my_icon_folder/icon_name" } , mySecondId: {...} }
    entries: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    labelClasses: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    tooltip: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    nullable: {
      type: Boolean,
      default: false,
    },
    dropDownClass: {
      type: String,
      default: "",
    },
    listItemClass: {
      type: String,
      default: "text-blue-800",
    },
    listItemTitleClass: {
      type: String,
      default: "",
    },
    selectedItemClass: {
      type: String,
      default: "bg-blue-200",
    },
    multiselectClass: {
      type: String,
      default: "",
    },
    /**
     * Function to filter an entry list with a given terms
     *
     * @param {Array} entityList - full list of th entity
     * @param {String} searchTerms - single string that contains the terms entered in the dropdown
     * @return {Array} - The list of filtered entityList
     *
     * @default Undefined, and searchable will not be enabled on the dropdown
     */
    filter: {
      type: Function,
      default: undefined,
    },
    showLabels: {
      type: Boolean,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: "bottom",
    },
    maxHeight: {
      type: Number,
      default: 300,
    },
  },
  computed: {
    isSearchable() {
      return !!this.filter;
    },
    readOnlyValue() {
      return this.entries[this.localValue]?.name;
    },
    filteredOptions() {
      return this.filteredEntries;
    },
    showIconInOption() {
      return (option) => this.entryIcon(option.id)?.length;
    },
  },
  data() {
    return {
      isOpen: false,
      localEntries: Object.values(this.entries),
      filteredEntries: Object.values(this.entries),
      localValue: cloneDeep(this.value),
      searchTerms: null,
    };
  },
  methods: {
    onChangeSearch(terms) {
      // If search is not enabled reset the list
      if (!this.isSearchable || !terms) {
        this.filteredEntries = this.localEntries;
        return;
      }
      this.searchTerms = terms;
      this.filteredEntries = this.filter(this.localEntries, terms);
    },
    entryIcon(id) {
      return this.entries[id] !== undefined ? this.entries[id].icon : "";
    },
    entryName(option) {
      return this.entries[option] === undefined
        ? option.name
        : this.entries[option].name;
    },
    onOptionSelected(option) {
      this.$emit("select", option.id === "undefined" ? null : option.id);
      // make v-model binding work
      this.$emit("input", option.id === "undefined" ? null : option.id);
    },
  },
  watch: {
    entries() {
      const possibleValues = Object.values(this.entries);
      if (this.nullable && possibleValues) {
        return [
          {
            id: "unset",
            name: tr("Common.notSet"),
          },
        ].concat(possibleValues);
      }
      this.localEntries = possibleValues;

      // Update search when entries have changed
      this.onChangeSearch(this.searchTerms);
    },
    value() {
      this.localValue = cloneDeep(this.value);
    },
  },
};
</script>
